import { Injectable } from '@angular/core';
import { NotificationService } from '../../shared-modules/notification/services/notification.service';
import { ConfigService } from '../config.service';
import { SocketEvents } from './socket.enum';
import { Subject } from 'rxjs';
import { LoginService } from '@ebw-monorepo/login';
import { EbwModalService } from '@ebw-monorepo/modal';
import { AuthService } from '../auth.service';

@Injectable({
  providedIn: 'root'
})
export class SocketService {
  private socket: WebSocket | null = null;
  private isConnected = false;
  private reconnectAttempts = 0;
  private readonly maxReconnectAttempts = 6;

  public message$ = new Subject<any>(); // RxJS Subject per i messaggi

  constructor(
    private configService: ConfigService,
    private notificationService: NotificationService,
    private ebwModalService: EbwModalService,
    private authService: AuthService
  ) {}

  // ------------------------------------------------------------------------------------------------

  init(autoConnect = true) {
    const websocketConfig = this.configService.getWebSocketConfiguration();
    if (!websocketConfig) {
      return console.log('Missing websocket config');
    }
    const url = websocketConfig.content.value;
    const userId = sessionStorage.getItem(LoginService.USER_ID_KEY);
    const fullUrl = `${url}?user_id=${encodeURIComponent(userId)}`;

    if (!url) {
      return console.log('URL not valid for ws');
    }

    this.socket = new WebSocket(fullUrl);

    if (autoConnect) {
      this.connect();
    }
  }

  // ------------------------------------------------------------------------------------------------

  connect(): void {
    if (!this.socket) return;

    this.socket.onopen = () => {
      this.isConnected = true;
      this.reconnectAttempts = 0;
      console.log('[SocketService] WebSocket Connected');
    };

    this.socket.onclose = (event) => {
      this.isConnected = false;
      console.warn('[SocketService] WebSocket Disconnected', event);

      if (!event.wasClean) {
        this.tryReconnect();
      }
    };

    this.socket.onerror = (error) => {
      console.error('[SocketService] WebSocket Error', error);

      // Se il socket è chiuso o in stato errato, tentiamo la riconnessione
      if (this.socket?.readyState === WebSocket.CLOSING || this.socket?.readyState === WebSocket.CLOSED) {
        this.tryReconnect();
      }
    };

    this.socket.onmessage = (event) => {
      this.handleMessage(event.data);
    };
  }

  // ------------------------------------------------------------------------------------------------

  disconnect(): void {
    if (this.socket) {
      this.socket.close();
      this.socket = null;
      this.isConnected = false;
    }
  }

  // ------------------------------------------------------------------------------------------------

  private tryReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('[SocketService] Max reconnect attempts reached');
      return;
    }

    this.reconnectAttempts++;
    const delay = Math.pow(2, this.reconnectAttempts) * 1000;

    console.log(`[SocketService] Reconnect attempt ${this.reconnectAttempts} in ${delay}ms`);

    setTimeout(() => {
      this.init(true);
    }, delay);
  }

  // ------------------------------------------------------------------------------------------------

  private handleMessage(data: string): void {
    try {
      const message: { event: string; message: string; recipient?: string } = JSON.parse(data);

      // Emit via RxJS Subject
      this.message$.next(message);

      switch (message.event) {
        case SocketEvents.AuthRequired:
          console.log('Server require authentication', message);
          this.socket.send(
            JSON.stringify({
              event: SocketEvents.AuthRequired,
              token: sessionStorage.getItem(LoginService.AUTH_TOKEN_KEY)
            })
          );
          break;
        case SocketEvents.Export:
        case SocketEvents.PasswordNotification:
        case SocketEvents.Plot:
        case SocketEvents.ExportDXF:
        case SocketEvents.Error:
          this.notificationService.getNotifications();
          break;
        case SocketEvents.Logout:
          this.ebwModalService.openInfoModal({ title: 'Logout', content: message.message });
          sessionStorage.removeItem(LoginService.AUTH_TOKEN_KEY);
          this.authService._logoutLocal();
          break;

        default:
          console.warn('[SocketService] Unknown event:', message.event);
      }
    } catch (err) {
      console.error('[SocketService] Failed to parse message:', data);
    }
  }

  // ------------------------------------------------------------------------------------------------

  emitEvent(event: SocketEvents, data: any = {}): void {
    if (this.socket && this.isConnected) {
      try {
        const payload = JSON.stringify({ event, data });
        this.socket.send(payload);
      } catch (err) {
        console.error('[SocketService] Failed to send message:', err);
      }
    } else {
      console.warn('[SocketService] Cannot emit, socket not connected');
    }
  }

  // ------------------------------------------------------------------------------------------------
}
